import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const POINTS = [
    "Invoices, bills, expenses and payments connected to every job",
    "A live management P&L — no end-of-month spreadsheet rebuild",
    "Profitability visible by job, customer, team or depot",
    "Management reports ready when you are",
];

const PL_ROWS = [
    { label: "Revenue", value: "£86,400", width: "100%", color: "#00E5FF" },
    { label: "Direct costs", value: "£41,220", width: "48%", color: "#0066FF" },
    { label: "Overheads", value: "£18,300", width: "21%", color: "#A855F7" },
];

const PnlPanel = () => (
    <div className="relative flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md" data-testid="finance-panel">
        <span className="absolute right-3 top-3 rounded-full border border-white/10 bg-[#0B1220]/80 px-2 py-0.5 font-mono-brand text-[8px] uppercase tracking-[0.2em] text-[#64748B]">
            Concept visual — sample data
        </span>
        <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-white">Management P&amp;L</p>
            <span className="rounded-full bg-[#00E5FF]/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#00E5FF]">
                Live
            </span>
        </div>
        <div className="mt-5 space-y-4">
            {PL_ROWS.map((r) => (
                <div key={r.label}>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="font-semibold text-[#94A3B8]">{r.label}</span>
                        <span className="font-bold text-white">{r.value}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                        <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: r.width }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: EASE, delay: 0.3 }}
                            className="h-full rounded-full"
                            style={{ background: r.color }}
                        />
                    </div>
                </div>
            ))}
        </div>
        <div className="mt-5 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3">
            <span className="text-[11px] font-semibold text-[#94A3B8]">Net margin</span>
            <span className="font-display text-lg font-extrabold text-brand-gradient">31%</span>
        </div>
        <div className="mt-4 space-y-2">
            {[
                ["Bills awaiting approval", "6"],
                ["Expenses to reconcile", "11"],
                ["Overdue invoices", "2"],
            ].map(([label, n]) => (
                <div key={label} className="flex items-center justify-between rounded-lg border border-white/[0.07] bg-white/[0.03] px-3.5 py-2.5">
                    <span className="text-[11px] text-[#B8C8DF]">{label}</span>
                    <span className="rounded-full bg-[#0066FF]/15 px-2 py-0.5 text-[10px] font-bold text-[#00E5FF]">{n}</span>
                </div>
            ))}
        </div>
        <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="mt-4 flex items-center gap-2.5 rounded-xl border border-[#A855F7]/20 bg-gradient-to-r from-[#A855F7]/[0.08] to-[#0066FF]/[0.08] p-3"
        >
            <Sparkles className="h-4 w-4 shrink-0 text-[#A855F7]" />
            <p className="text-[11px] text-[#B8C8DF]">
                <span className="font-bold text-white">AI flag:</span> parts spend is 12% above trend on maintenance jobs this month.
            </p>
        </motion.div>
    </div>
);

export default function HomeFinance() {
    return (
        <section
            className="relative overflow-hidden bg-[#0A0F1C]"
            data-testid="home-finance-section"
            aria-labelledby="home-finance-heading"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(circle at 8% 15%, rgba(0,102,255,0.14) 0%, transparent 42%), radial-gradient(circle at 92% 90%, rgba(168,85,247,0.1) 0%, transparent 45%)",
                }}
            />
            <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-6 py-24 sm:py-32 lg:grid-cols-2 lg:gap-16">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="order-2 lg:order-1"
                >
                    <PnlPanel />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                    className="order-1 lg:order-2"
                >
                    <span
                        className="inline-flex items-center gap-2 rounded-full border border-[#0066FF]/30 bg-[#0066FF]/10 px-4 py-1.5"
                        data-testid="home-finance-eyebrow"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#93C5FD] sm:text-xs">
                            Finance &amp; Reporting
                        </span>
                    </span>
                    <h2
                        id="home-finance-heading"
                        className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-white sm:text-5xl"
                        data-testid="home-finance-heading"
                    >
                        From Daily Operations to{" "}
                        <span className="text-brand-gradient">Financial Clarity.</span>
                    </h2>
                    <p className="mt-6 max-w-lg text-base leading-relaxed text-[#94A3B8] sm:text-lg" data-testid="home-finance-intro">
                        Because jobs, sales, stock and the till already live in TedOps, the numbers
                        build themselves — bills, expenses and job activity flow straight into a live
                        management P&amp;L and reports you can actually run the business on.
                    </p>
                    <ul className="mt-8 space-y-3">
                        {POINTS.map((p) => (
                            <li key={p} className="flex items-start gap-3 text-[15px] font-medium text-[#E2E8F0]">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00E5FF]" />
                                {p}
                            </li>
                        ))}
                    </ul>
                    <a
                        href="mailto:enquires@tedopsai.com?subject=Book%20a%20TedOps%20AI%20demo"
                        className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                        data-testid="home-finance-cta"
                    >
                        See Finance in Action
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
